/* eslint-disable @typescript-eslint/naming-convention */
import { useEffect } from 'react'
import { useParams, useOutletContext } from 'react-router-dom'
import { useComputed, useSignal } from '@preact/signals-react'

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import { Container, Col, Row, Button } from 'react-bootstrap'
import Form from 'react-bootstrap/Form'

import Loadable from '../../components/Loadable.jsx'
import PageTitle from '../../components/PageTitle.jsx'

import { NotificationTypes, addNotification, notifyException } from '../../state/actions/notificationActions.js'
import { fetchPasswordSettings } from '../../state/actions/passwordSettingsActions.js'
import { fetchUserByAccessToken } from '../../state/actions/userActions.js'
import { fetchUserManifestByAccessToken } from '../../state/actions/userManifestActions.js'
import { fetchUserProfile, updateUserProfile } from '../../state/actions/userProfileActions.js'

import { passwordSettingsSignal, passwordSettingsLoadedSignal, passwordSettingsErrorSignal } from '../../state/signals/passwordSettingsSignals.js'
import { userProfileSignal, userProfileLoadedSignal, userProfileErrorSignal } from '../../state/signals/userProfileSignals.js'
import { debugSignal } from '../../state/signals/userSettingsSignals.js'

import { validateForm, validateInput } from '../../utils/form.js'
import { REGEX_VALID_EMAIL_ADDRESS } from '../../utils/RegularExpressions.js'
import { scrollToTop } from '../../utils/utils.js'

const MyProfilePage = () => {

	const { beId } = useParams()
	const { user, config, accessToken, userPermissions, userPermissionsSummary, accordionData: sidebarAccordionData } = useOutletContext()

	const emailAddressSignal = useSignal(null)
	const existingPasswordSignal = useSignal(null)
	const newPasswordSignal = useSignal(null)
	const securityImageSignal = useSignal(null)
	const verifiedPasswordSignal = useSignal(false)

	const passwordErrorListSignal = useSignal({})
	const emailErrorListSignal = useSignal({})

	const passwordReqMinimumClassNameSignal = useSignal(undefined)
	const passwordReqLetterClassNameSignal = useSignal(undefined)
	const passwordReqUppercaseClassNameSignal = useSignal(undefined)
	const passwordReqLowercaseClassNameSignal = useSignal(undefined)
	const passwordReqNumberClassNameSignal = useSignal(undefined)
	const passwordReqSpecialClassNameSignal = useSignal(undefined)
	const passwordReqUsernameClassNameSignal = useSignal(undefined)
	const passwordReqHistoryClassNameSignal = useSignal(undefined)

	const newPasswordValidSignal = useSignal(false)
	const confirmPasswordValidSignal = useSignal(false)
	const emailAddressValidSignal = useSignal(false)
	const confirmEmailAddressValidSignal = useSignal(false)

	useEffect(() => {
		fetchPasswordSettings(beId, config, accessToken)
		fetchUserProfile(user.id, config, accessToken).then(
			(userProfile) => {
				emailAddressSignal.value = userProfile.emailAddress
				securityImageSignal.value = userProfile.securityImage
			}
		)
	}, [beId, user.id, emailAddressSignal, securityImageSignal, config, accessToken])

	/*useEffect(
		() => {
			if (!userProfileLoadedSignal.value) {
				emailAddressSignal.value = 'Loading...'
			} else {
				emailAddressSignal.value = userProfileSignal.value.emailAddress
			}
		}
	)*/

	const selectImage = (id) => {
		const f = "selectImage()"
		//window.alert(`${f}: entered`)
		securityImageSignal.value = id
	}

	const resetPasswordSignals = () => {
		passwordErrorListSignal.value = {}
		passwordReqMinimumClassNameSignal.value = undefined
		passwordReqLetterClassNameSignal.value = undefined
		passwordReqUppercaseClassNameSignal.value = undefined
		passwordReqLowercaseClassNameSignal.value = undefined
		passwordReqNumberClassNameSignal.value = undefined
		passwordReqSpecialClassNameSignal.value = undefined
		passwordReqUsernameClassNameSignal.value = undefined
		passwordReqHistoryClassNameSignal.value = undefined
		newPasswordValidSignal.value = false
	}

	const validators = {
		// XXX this one is only possible if the current password field is present and filled
		/*validateNewPassword: (input) => {
			//make sure it's not the same as the current password
			const pass = input.value
		},*/
		validatePasswordSettings: () => {
			const f = "ValidatePasswordSettings()"
			let print = false
			//generate validators from passwordSettings; update elements for missing components
			let valid = true
			let input = document.getElementById("newPassword")
			//passwordReqMinimum, passwordReqLetter, passwordReqUppercase, passwordReqLowercase, passwordReqNumber, passwordReqSpecial, passwordReqUsername, passwordReqHistory
			if (input.value.length == 0) {
				if (print) {
					console.log(`${f}: Input field is empty`)
				}
				resetPasswordSignals()
				input.setAttribute('data-msg', "Enter a new password.")
				return false
			}
			let errors = []
			//minimum X characters
			if (passwordSettingsSignal.value.minimumNumberOfCharacters && passwordSettingsSignal.value.minimumNumberOfCharacters > 0) {
				const length = input.value.length
				if (length < passwordSettingsSignal.value.minimumNumberOfCharacters) {
					if (print) {
						console.log(`${f}: Password is too short at ${length} characters`)
					}
					//input.setAttribute('data-msg', `Your password must be at least ${passwordSettingsSignal.value.minimumNumberOfCharacters} characters long.`) //Error message is taken care of by naturalValidateInput
					passwordReqMinimumClassNameSignal.value = 'invalid'
					valid = false
				} else {
					if (print) {
						console.log(`${f}: Password length is adequate at ${length} characters`)
					}
					passwordReqMinimumClassNameSignal.value = 'valid'
					delete passwordErrorListSignal.value.minLength
				}
			} else if (print) {
				console.log(`${f}: Password requirements do not specify a minimum length`)
			}
			//a letter(a - z or A - Z)
			if (passwordSettingsSignal.value.containsLetters) {
				if (!/[a-z]/i.test(input.value)) {
					if (print) {
						console.log(`${f}: Password contains no letters`)
					}
					errors.push("Your password must contain at least one letter (A-Z or a-z).")
					passwordReqLetterClassNameSignal.value = 'invalid'
					valid = false
				} else {
					if (print) {
						console.log(`${f}: Password contains at least one letter`)
					}
					passwordReqLetterClassNameSignal.value = 'valid'
					delete passwordErrorListSignal.value.letters
				}
			} else if (print) {
				console.log(`${f}: Password does not require letters`)
			}
			//an uppercase letter(A - Z)
			if (passwordSettingsSignal.value.containsUppercaseLetters) {
				if (!/[A-Z]/.test(input.value)) {
					if (print) {
						console.log(`${f}: Password contains at least one upper case letter`)
					}
					errors.push("Your password must contain at least one upper case letter (A-Z).")
					passwordReqUppercaseClassNameSignal.value = 'invalid'
					valid = false
				} else {
					if (print) {
						console.log(`${f}: Password contains no upper case letters`)
					}
					passwordReqUppercaseClassNameSignal.value = 'valid'
					delete passwordErrorListSignal.value.uppercase
				}
			} else if (print) {
				console.log(`${f}: Password does not require any upper case characters`)
			}
			//a lowercase letter(a - z)
			if (passwordSettingsSignal.value.containsLowercaseLetters) {
				if (!/[a-z]/.test(input.value)) {
					if (print) {
						console.log(`${f}: Password has at least one lower case letter`)
					}
					errors.push("Your password must contain at least one lower case letter (a-z).")
					passwordReqLowercaseClassNameSignal.value = 'invalid'
					valid = false
				} else {
					if (print) {
						console.log(`${f}: Password contains no lower case letters`)
					}
					passwordReqLowercaseClassNameSignal.value = 'valid'
					delete passwordErrorListSignal.value.lowercase
				}
			} else if (print) {
				console.log(`${f}: Password does not require any lower case letters`)
			}
			//a number
			if (passwordSettingsSignal.value.containsNumbers) {
				if (!/[0-9]/.test(input.value)) {
					if (print) {
						console.log(`${f}: Password contains at least one digit`)
					}
					errors.push("Your password must contain at least one digit (0-9).")
					passwordReqNumberClassNameSignal.value = 'invalid'
					valid = false
				} else {
					if (print) {
						console.log(`${f}: Password does not contain any digits`)
					}
					passwordReqNumberClassNameSignal.value = 'valid'
					delete passwordErrorListSignal.value.digits
				}
			} else if (print) {
				console.log(`${f}: Password does not require any digits`)
			}
			//a special character(non - alphanumeric)
			if (passwordSettingsSignal.value.containsSpecialCharacters) {
				if (!/[^a-zA-Z0-9]/.test(input.value)) {
					if (print) {
						console.log(`${f}: Password contains at least one special character`)
					}
					errors.push("Your password must contain at least one non-alphanumeric character.")
					passwordReqSpecialClassNameSignal.value = 'invalid'
					valid = false
				} else {
					if (print) {
						console.log(`${f}: Password does not contain any special characters`)
					}
					passwordReqSpecialClassNameSignal.value = 'valid'
					delete passwordErrorListSignal.value.nonalphanumeric
				}
			}
			//does not contain your username
			if (passwordSettingsSignal.value.doesNotContainUsername) {
				if (input.value.toLowerCase().includes(userProfileSignal.value.username.toLowerCase())) {
					if (print) {
						console.log(`${f}: Username is not part of the password`)
					}
					errors.push("Your username cannot be part of your password.")
					passwordReqUsernameClassNameSignal.value = 'invalid'
					valid = false
				} else {
					if (print) {
						console.log(`${f}: Username is part of the password`)
					}
					passwordReqUsernameClassNameSignal.value = 'valid'
					delete passwordErrorListSignal.value.username
				}
			} else if (print) {
				console.log(`${f}: Password is permitted to contain username.`)
			}
			if (valid) {
				input.setAttribute('data-msg', errors.join("\n"))
			} else {
				input.removeAttribute(errors)
			}
			if (print) {
				if (valid) {
					console.log(`${f}: All validation passed successfully`)
				} else {
					console.log(`${f}: Validation failed`)
				}
			}
			newPasswordValidSignal.value = valid
			return valid
		},
		validateConfirmPassword: (input) => { // done
			const f = "validateConfirmPassword()"
			let print = false
			//make sure the two fields match
			const pass = document.getElementById("newPassword").value
			const confirm = document.getElementById("confirmPassword").value
			if (print) {
				console.log(`${f}: password "${pass}", confirm password "${confirm}"`)
			}
			if (pass != confirm) {
				passwordErrorListSignal.value['confirm'] = input.getAttribute("data-msg")
				confirmPasswordValidSignal.value = false
				if (print) {
					console.log(`${f} confirm password failed`)
				}
				return false
			}
			delete passwordErrorListSignal.value.confirm
			if (print) {
				console.log(`${f} confirm password validated successfully`)
			}
			confirmPasswordValidSignal.value = true
			return true
		},
		validateEmailAddress: (input) => { // done
			const f = "validateEmailAddress()"
			let print = false
			if (!userProfileLoadedSignal.value) {
				if (print) {
					console.log(`${f}: user profile was not loaded`)
				}
				return false
			}
			let valid = true
			//validate by regex
			const emailRegex = REGEX_VALID_EMAIL_ADDRESS
			if (!emailRegex.test(input.value)) {
				if (print) {
					console.log(`${f}: Email address failed regex validation`)
				}
				input.setAttribute("data-msg", "Please confirm your email address was entered correctly in both the Email Address and Confirm Email Address fields.")
				valid = false
			} else {
				if (print) {
					console.log(`${f}: Email address passed regex validation`)
				}
				input.removeAttribute('data-msg')

			}
			if (user.emailAddressVerified && input.value == userProfileSignal.value.emailAddress) {
				if (print) {
					console.log(`${f}: email address is the same as the last time the user profile was loaded`)
				}
				emailErrorListSignal.value['unchanged'] = "Your email address has not changed."
				valid = false
			} else {
				if (print) {
					console.log(`${f}: email address is not verified, or has changed since the last time the user profile was loaded`)
				}
				delete emailErrorListSignal.value.unchanged
			}
			emailAddressValidSignal.value = valid
			if (print) {
				if (valid) {
					console.log(`${f}: New email address passed validation`)
				} else {
					console.log(`${f}: New email address failed validation`)
				}
			}
			return valid
		},
		validateConfirmEmailAddress: (input) => { // done
			const f = `validateConfirmEmailAddress()`
			let print = false
			//make sure the two fields match
			const email = document.getElementById("emailAddress").value
			const confirm = document.getElementById("confirmEmailAddress").value
			if (confirm.length == 0) {
				if (print) {
					console.log(`${f} confirm email address field is blank`)
				}
				confirmEmailAddressValidSignal.value = false
				return false
			} else if (print) {
				console.log(`${f} confirm email address field has the value "${confirm}"`)
			}
			let valid = true
			if (email != confirm) {
				if (print) {
					console.log(`${f}: Email address and confirmation do not match`)
				}
				emailErrorListSignal.value['confirm'] = input.getAttribute("data-msg")
				valid = false
			} else {
				if (print) {
					console.log(`${f}: Confirm email address is valid`)
				}
				delete emailErrorListSignal.value.confirm
			}
			confirmEmailAddressValidSignal.value = valid
			if (valid && emailAddressValidSignal.value && userProfileLoadedSignal.value) {
				console.log("You should be able to change your email address now")
			}
			return valid

		}
	}

	const changePassword = async (event) => {
		event.preventDefault()
		const f = "changePassword()"
		const form = event.target
		if (validateForm(form, validators)) {
			let userProfile = {
				emailAddress: null,
				emailAddressVerified: userProfileSignal.value.emailAddressVerified,
				existingPassword: existingPasswordSignal.value,
				imageSelected: userProfileSignal.value.imageSelected,
				isUserPassword: userProfileSignal.value.isUserPassword,
				newEmailAddress: null,
				newEmailAddressDirty: false,
				newPassword: newPasswordSignal.value,
				securityImage: securityImageSignal.value,
				securityImageDirty: false,
				status: userProfileSignal.value.status,
				username: userProfileSignal.value.username
			}
			document.getElementById("newPassword").value = null
			document.getElementById("confirmPassword").value = null
			document.getElementById("currentPassword").value = null
			resetPasswordSignals()
			confirmPasswordValidSignal.value = false
			await updateUserProfile(userProfileSignal.value.id, userProfile, config, accessToken).then(
				() => {
					addNotification("Your password was updated successfully.", NotificationTypes.SUCCESS, true, "Password Changed")
					scrollToTop("main")
				}
			).catch(
				(err) => {
					notifyException(err, "Error Updating Password")
				}
			)
		}
	}

	/**
	 * Update the user's profile image. This function does not benefit from form validation, just make sure the selected image valis is a number 1-16
	 * @param {any} event
	 */
	const changeProfileImage = async (event) => {
		event.preventDefault()
		const f = "changeProfileImage()"
		const userProfile ={
			emailAddress: null,
			emailAddressVerified: userProfileSignal.value.emailAddressVerified,
			existingPassword: null,
			imageSelected: true,
			isUserPassword: userProfileSignal.value.isUserPassword,
			newEmailAddress: null,
			newEmailAddressDirty: false,
			newPassword: null,
			securityImage: securityImageSignal.value,
			securityImageDirty: true,
			status: userProfileSignal.value.status,
			username: userProfileSignal.value.username
		}
		await updateUserProfile(userProfileSignal.value.id, userProfile, config, accessToken).then(
			async () => {
				await Promise.all([
					fetchUserByAccessToken(config, accessToken), //needed because setting your password for the first time changes the valid of user.isUserPassword to true
					fetchUserManifestByAccessToken(config, accessToken, true)
				]).then(
					() => {
						addNotification("Your profile image has been updated.", NotificationTypes.SUCCESS, true, "Profile Image Changed")
						scrollToTop("main")
					}
				).catch(
					(err) => {
						notifyException(err, "Error Updating Profile Image")
					}
				)
			}
		)
	}

	const changeEmailAddress = async (event) => {
		event.preventDefault()
		const f = "changeEmailAddress()"
		let print = false
		const form = event.target
		if (validateForm(form, validators)) {
			let userProfile = {
				emailAddress: emailAddressSignal.value,
				emailAddressVerified: userProfileSignal.value.emailAddressVerified,
				existingPassword: null,
				imageSelected: userProfileSignal.value.imageSelected,
				isUserPassword: userProfileSignal.value.isUserPassword,
				newEmailAddress: emailAddressSignal.value,
				newEmailAddressDirty: true,
				newPassword: null,
				securityImage: securityImageSignal.value,
				securityImageDirty: false,
				status: userProfileSignal.value.status,
				username: userProfileSignal.value.username
			}
			const email = emailAddressSignal.value
			if (print) {
				console.log(`${f}: email address signal value "${email}"`)
			}
			await updateUserProfile(userProfileSignal.value.id, userProfile, config, accessToken).then(
				() => {
					document.getElementById("confirmEmailAddress").value = null
					addNotification(`Your email address was changed to "${email}".`, NotificationTypes.SUCCESS, true, "Email Address Changed")
					scrollToTop("main")
				}
			).catch(
				(err) => {
					notifyException(err, "Error Updating Email Address")
				}
			)
		}
	}

	const SecurityImageSelect = ({ securityImages }) => {
		let images = []
		//console.log("Received the following image objects:")
		//console.log(securityImages)
		for (let i = 0; i < securityImages.length; i++) {
			const image = securityImages[i]
			images.push(
				<span className="tile" key={image.id} onClick={() => { selectImage(image.id) }} data-current={securityImageSignal.value == image.id ? "true" : undefined} >
					<img src={image.img} id={image.elementId}/>
				</span>
			)
		}
		//console.log("Returning the following security images:")
		//console.log(images)
		return images
	}

	const securityImagesComputed = useComputed(
		() => {
			const url = `${config.ssoBaseUrl}/images/security`
			let images = []
			for (let i = 1; i < 17; i++) {
				images.push({
					id: i,
					img: `${url}/${i}.png`,
					elementId: `img${i}`
				})
			}
			//console.log("Generated the following images array:")
			//console.log(images)
			return images
		}
	)

	const validate = async (event) => {
		return await validateInput(event.target, validators, event.type)
	}

	const canEditComputed = useComputed(
		() => {
			return true
		}
	)

	return (
		<div className="m-3">
			<Breadcrumb className="px-3 pt-1 bg-light">
				<Breadcrumb.Item active>My Profile</Breadcrumb.Item>
			</Breadcrumb>
			<PageTitle title={`${user.firstName} ${user.lastName}`} canEditSignal={canEditComputed} />
			<Form onSubmit={changePassword} className="bg-light p-4 mb-4">
				<h4>Password</h4>
				{
					!user.isUserPassword && (
						<div>You are currently signed in using a temporary password. Please use this form to set a permanent password that satifies the requirements psecified below.</div>
					)
				}
				<Container><Row>

					<Col md={6}>
						<Form.Group className="mb-2">
							<Form.Label>Current Password</Form.Label>
							<div>
								{
									!verifiedPasswordSignal.value ? (
										<input
											type='password'
											placeholder=''
											className='form-control'
											id='currentPassword'
											name='currentPassword'
											maxLength='50'
											autoCapitalize="off"
											autoCorrect="off"
											spellCheck="false"
											required={true}
											data-msg="You must enter your current password."
											onChange={
												(event) => {
													existingPasswordSignal.value = event.target.value
												}
											}
											onKeyUp={
												(event) => {
													existingPasswordSignal.value = event.target.value
												}
											}
											disabled={!userProfileLoadedSignal.value}
										/>
									) : (
										<input
											placeholder='Provided and Verified'
											className='form-control'
											id='currentPassword'
											name='currentPassword'
											maxLength='50'
											disabled='true'
											autoCapitalize="off"
											autoCorrect="off"
											spellCheck="false"
										/>
									)
								}
							</div>
						</Form.Group>
						<Form.Group className="mb-2">
							<Form.Label>New Password</Form.Label>
							<div>
								<input
									type='password'
									placeholder=''
									id='newPassword'
									name='newPassword'
									maxLength='50'
									minLength={passwordSettingsSignal.value.minimumNumberOfCharacters}
									autoCapitalize="off"
									autoCorrect="off"
									spellCheck="false"
									required={true}
									className="form-control"
									onChange={
										(event) => {
											newPasswordSignal.value = event.target.value
											validate(event)
										}
									}
									onKeyUp={
										(event) => {
											newPasswordSignal.value = event.target.value
											validate(event)
										}
									}
									data-validators={JSON.stringify({
										change: ["validatePasswordSettings", "validateConfirmPassword"],
										keyup: ["validatePasswordSettings", "validateConfirmPassword"]
									})}
									disabled={!userProfileLoadedSignal.value}
								/>
							</div>
						</Form.Group>
						<Form.Group className="mb-2">
							<Form.Label>Confirm Password</Form.Label>
							<div>
								<input
									type='password'
									placeholder=''
									id='confirmPassword'
									name='confirmPassword'
									maxLength='50'
									minLength={passwordSettingsSignal.value.minimumNumberOfCharacters}
									autoCapitalize="off"
									autoCorrect="off"
									spellCheck="false"
									required={true}
									data-msg="The passwords you entered do not match."
									className="form-control"
									onChange={validate}
									onKeyUp={validate}
									data-validators={JSON.stringify({
										change: ["validatePasswordSettings", "validateConfirmPassword"],
										keyup: ["validatePasswordSettings", "validateConfirmPassword"]
									})}
									disabled={!userProfileLoadedSignal.value}
								/>
							</div>
						</Form.Group>
					</Col>
					
					<Col md="auto">
						<Loadable loadedSignals={[passwordSettingsLoadedSignal]} errorSignals={[passwordSettingsErrorSignal]}>
							<div>Passwords must meet the following requirements:</div>
							<ul id="passwordRequirements">
								<li className={passwordReqMinimumClassNameSignal.value}>minimum {passwordSettingsSignal.value.minimumNumberOfCharacters} characters</li>
								{
									passwordSettingsSignal.value.containsLetters && (<>
										<li className={passwordReqLetterClassNameSignal.value}>a letter (a-z or A-Z)</li>
										{
											passwordSettingsSignal.value.containsUppercaseLetters && (
												<li className={passwordReqUppercaseClassNameSignal.value}>an uppercase letter (A-Z)</li>
											)
										}
										{
											passwordSettingsSignal.value.containsLowercaseLetters && (
												<li className={passwordReqLowercaseClassNameSignal.value}>a lowercase letter (a-z)</li>
											)
										}
									</>)
								}
								{
									passwordSettingsSignal.value.containsNumbers && (
										<li className={passwordReqNumberClassNameSignal.value}>a number</li>
									)
								}
								{
									passwordSettingsSignal.value.containsSpecialCharacters && (
										<li className={passwordReqSpecialClassNameSignal.value}>a special character (non-alphanumeric)</li>
									)
								}
								{
									passwordSettingsSignal.value.doesNotContainUsername && (
										<li className={passwordReqUsernameClassNameSignal.value}>does not contain your username</li>
									)
								}
								{
									(passwordSettingsSignal.value.enforcePasswordHistoryForLastPasswords && passwordSettingsSignal.value.enforcePasswordHistoryForLastPasswords > 0) && (
										<li className={passwordReqHistoryClassNameSignal.value}>cannot be the last {passwordSettingsSignal.value.enforcePasswordHistoryForLastPasswords} passwords used</li>
									)
								}
							</ul>
						</Loadable>
					</Col>
				</Row></Container>
				<div>

					<div id="errors" style={{ height: '15px', color: '#c00', margin: '5px 0 0 13%' }}></div>
					<Button variant={newPasswordValidSignal.value && confirmPasswordValidSignal.value ? 'primary' : 'text'} size="md" type="submit" disabled={!(userProfileLoadedSignal.value && newPasswordValidSignal.value && confirmPasswordValidSignal.value)}>Change Password</Button>
				</div>
			</Form>
			<Form onSubmit={changeEmailAddress} className="bg-light p-4 mb-4">
				<h4>Email Address</h4>
				<div>
					<p>The email entered will be used when you attempt to retrieve your forgotten username or reset your password. Email addresses must be less than 200 characters and in the following format: email@domain.com.</p>
						<Form.Group className="mb-2">
							<Form.Label>Email Address</Form.Label>
								<input
									defaultValue={emailAddressSignal.value}
									type='email'
									placeholder='email@domain.com'
									className='form-control'
									id='emailAddress'
									name='emailAddress'
									maxLength='200'
									autoCapitalize="off"
									autoCorrect="off"
									spellCheck={false}
									required={true}
									data-msg="Invalid email address."
									onChange={
										(event) => {
											emailAddressSignal.value = event.target.value
											console.log(`Updated email address signal value to ${emailAddressSignal.value}`)
											validate(event)
										}
									}
									onKeyUp={
										(event) => {
											emailAddressSignal.value = event.target.value
											console.log(`Updated email address signal value to ${emailAddressSignal.value}`)
											validate(event)
										}
									}
									data-validators={JSON.stringify({
										change: ["validateEmailAddress", "validateConfirmEmailAddress"],
										keyup: ["validateEmailAddress", "validateConfirmEmailAddress"]
									})}
									disabled={!userProfileLoadedSignal.value}
								/>
						</Form.Group>
						<Form.Group className="mb-2">
							<Form.Label>Confirm Email Address</Form.Label>
								<input
									value={user.emailAddress}
									type='email'
									placeholder='email@domain.com'
									id='confirmEmailAddress'
									name='confirmEmailAddress'
									maxLength='200'
									autoCapitalize="off"
									autoCorrect="off"
									required={true}
									spellCheck={false}
									className="form-control"
									onChange={validate}
									onKeyUp={validate}
									data-validators={JSON.stringify({
										change: ["validateConfirmEmailAddress"],
										keyup: ["validateConfirmEmailAddress"]
									})}
								/>
						</Form.Group>
					<div id="errors" style={{ height: '15px', color: '#c00', margin: '5px 0 0 13%' }}></div>
					<Button variant={emailAddressValidSignal.value && confirmEmailAddressValidSignal.value ? 'primary' : 'text'} size="md" type="submit" disabled={(!userProfileLoadedSignal.value || !emailAddressValidSignal.value || !confirmEmailAddressValidSignal.value)}>{user.emailAddressVerified ? "Change" : "Confirm"} Email Address</Button>

				</div>

			</Form>
			<Form onSubmit={changeProfileImage} className="bg-light p-4 mb-4">
				<div style={{
					height: '30px',
					width: '30px',
					marginTop: '-15px',
					marginLeft: '-15px'
				}}
				></div>
				<h4>Profile Image</h4>
				<div>
					<div className="mb-2">The image selected will be displayed each time you are prompted to enter your password, and at the top of every page.</div>
					<div style={{ height: '168px'}}>
						<span style={{ position: 'relative', display: 'inline-block', height: '164px', width: '168px', verticalAlign: "top" }}>
							<SecurityImageSelect securityImages={securityImagesComputed.value} />
						</span>
						<span style={{ position: 'relative', display: 'inline-block', height: '168px', width: '168px', verticalAlign: "top" }}>
							{
								securityImageSignal.value ? (
									<img src={`${config.ssoBaseUrl}/images/security/${securityImageSignal.value}.png`} style={{ height: '100%', width: '100%' }} />
								) : (
									<span>Please select your profile image by clicking one of the icons to the left.</span>
								)
							}
						</span>
					</div>
					<div id="errors" style={{ height: '15px', color: '#c00', margin: '5px 0 0 13%' }} ></div>
					<Button variant={userProfileLoadedSignal.value && (securityImageSignal.value != userProfileSignal.value.securityImage) ? 'primary' : 'text'} size="md" type="submit" disabled={!(userProfileLoadedSignal.value && securityImageSignal.value != userProfileSignal.value.securityImage)}>Change Profile Image</Button>
				</div>
			</Form>
							{
					debugSignal.value && (
						<div>
							<div>Email address valid: {emailAddressValidSignal.value ? "true" : "false"}</div>
							<div>Confirm email address valid: {confirmEmailAddressValidSignal.value ? "true" : "false"}</div>
						</div>
					)
				}
					{
						debugSignal.value && (
							<div>
								<div>New password valid: {newPasswordValidSignal.value ? "true" : "false"}</div>
								<div>Confirm password valid: {confirmPasswordValidSignal.value ? "true" : "false"}</div>
							</div>
						)
					}
		</div >
	)
}
export default MyProfilePage